'use client';

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DynamicFormBuilder } from './DynamicFormBuilder';
import { type FormThemeConfig, resolveFormTheme } from './formThemes';
import type { FormConfiguration } from './formConfigurationUtils';
import { asObject, type FieldValue, type FormDataValues } from './formFieldTypes';
import { Loader2 } from 'lucide-react';

interface DynamicFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  formKey: string;
  title: string;
  mode?: 'create' | 'edit' | 'view';
  entityType?: string;
  entityId?: string | number;
  initialData?: FormDataValues;
  onSuccess?: (data: FormDataValues) => void;
  onSubmit?: (data: FormDataValues) => Promise<void>; // Custom submit handler
  customSchema?: FormConfiguration; // Custom form schema to override loaded config
  /** Optional form theme (from theme_config on form_configurations) */
  formTheme?: Partial<FormThemeConfig> | null;
  /** Optional subtitle override — when set, replaces the auto-generated subtitle */
  subtitle?: string;
}

export default function DynamicFormModal({
  isOpen,
  onClose,
  formKey,
  title,
  mode = 'create',
  entityType = 'employee',
  entityId,
  initialData = {},
  onSuccess,
  onSubmit,
  customSchema,
  formTheme,
  subtitle,
}: DynamicFormModalProps) {
  const [formData, setFormData] = useState<FormDataValues>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load entity data when in edit/view mode with entityId
  useEffect(() => {
    const loadEntityData = async () => {
      // Only load if we have an entityId and mode is edit/view, and modal is open
      if (!entityId || mode === 'create' || !isOpen) {
        return;
      }

      setLoading(true);
      try {
        // Get authentication token
        const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
        const token = tokens.access_token;

        if (!token) {
          console.error('No authentication token found');
          return;
        }

        let endpoint = '';
        
        // Determine endpoint based on entity type
        if (entityType === 'employee') {
          // Use the employee_id directly
          // The GET endpoint uses employee_id to fetch the employee
          endpoint = `/api/v1/employees/${entityId}`;
        } else if (entityType === 'user') {
          endpoint = `/api/v1/users/${entityId}`;
        } else if (entityType === 'invitation') {
          endpoint = `/api/v1/user_invitations/${entityId}`;
        } else {
          console.warn(`Unknown entity type: ${entityType}`);
          return;
        }

        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const result = await response.json();
          
          if (result.success) {
            let entityData: FormDataValues | null = null;
            
            if (entityType === 'employee') {
              // Get employee from single GET endpoint response
              const employee = result.employee;
              
              if (employee) {
                // Extract employee_id from the membership
                const employeeId = entityId; // This is the employee_id passed in
                
                // Load geofence assignments for this employee
                let geofenceAssignments: unknown[] = [];
                try {
                  const assignmentsResponse = await fetch(`/api/v1/time-attendance/config-assignments?employee_id=${employeeId}`, {
                    method: 'GET',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`,
                    },
                  });
                  
                  if (assignmentsResponse.ok) {
                    const assignmentsResult = await assignmentsResponse.json();
                    if (assignmentsResult.success && assignmentsResult.assignments) {
                      geofenceAssignments = assignmentsResult.assignments;
                    }
                  }
                } catch (error) {
                  console.error('Failed to load geofence assignments:', error);
                }
                
                // Use module_roles from the GET endpoint which includes shift_template_id
                // The GET /api/v1/employees/[id] endpoint already loads this data properly
                const employeeModules = employee.module_roles || [];
                
                console.log('[DynamicFormModal] Employee modules from GET endpoint:', employeeModules);
                
                // Flatten the employee data for form
                entityData = {
                  // Employee fields
                  employee_number: employee.employee_number,
                  first_name: employee.first_name,
                  last_name: employee.last_name,
                  email: employee.email,
                  phone: employee.phone,
                  job_title: employee.job_title,
                  department: employee.department,
                  start_date: employee.start_date,
                  employment_status: employee.employment_status,
                  employment_type: employee.employment_type,
                  location: employee.location,
                  salary: employee.salary,
                  // Termination fields
                  termination_type: employee.termination_type,
                  termination_reason: employee.termination_reason,
                  end_date: employee.end_date,
                  scheduled_termination_date: employee.scheduled_termination_date,
                  // Membership fields
                  employee_id: employeeId,
                  // Module access from GET endpoint (includes shift_template_id in module_roles)
                  modules: employeeModules,
                  module_roles: employeeModules, // Support both field names
                  // Shift info from GET endpoint
                  shift_template_id: employee.shift_template_id,
                  shift_name: employee.shift?.name,
                  // Face template info (read-only status display)
                  has_face_template: employee.has_face_template 
                    ? '✓ Active Face Template' 
                    : 'No Face Template',
                  face_template_count: employee.face_template_count,
                  // Geofence assignments
                  geofence_assignments: geofenceAssignments,
                  // Include any rule overrides that were extracted in GET endpoint
                  ...employee
                } as FormDataValues;
                
                console.log('[DynamicFormModal] Final entityData for employee:', {
                  shift_template_id: entityData.shift_template_id,
                  modules: entityData.modules,
                  module_roles: entityData.module_roles
                });
              }
            } else if (entityType === 'user') {
              // User API returns result.user with nested client_memberships and module_access
              // The GET /users/[id] endpoint flattens the first membership to top level
              const user = result.user;
              
              // Get user's module access from the API response
              // module_access is an array of {id, module_key, role, status, access_context, custom_permissions}
              // Map to format expected by module-checkbox-group or module-role-config: [{module_key, role, ...custom_permissions}]
              const userModules = (user.module_access as Array<{
                module_key: string;
                role: string;
                custom_permissions?: Record<string, unknown>;
              }> | undefined)?.map((m) => ({
                module_key: m.module_key,
                role: m.role,
                // Spread custom_permissions so conditional fields like is_field_worker are included
                ...(m.custom_permissions || {})
              })) || [];
              
              // The API flattens membership data to top level: client_role, department, job_title
              const userRole = user.client_role || user.role;
              
              entityData = {
                ...user,
                // Map client_role to both role and default_role for form
                role: userRole,
                default_role: userRole,
                // department and job_title are already at top level from API
                department: user.department,
                job_title: user.job_title,
                // Add module access (array of {module_key, role} objects)
                modules: userModules,
              } as FormDataValues;
              
              console.log('[DynamicFormModal] User entity data loaded:', {
                department: entityData.department,
                job_title: entityData.job_title,
                role: entityData.role,
                default_role: entityData.default_role,
                modules: entityData.modules,
                modulesWithCustomPerms: (user.module_access as Array<{ custom_permissions?: Record<string, unknown> }> | undefined)?.filter((m) => m.custom_permissions && Object.keys(m.custom_permissions).length > 0)
              });
            } else {
              // For other entity types, use the data directly
              entityData = (result.data || result) as FormDataValues;
            }
            
            if (entityData) {
              setFormData(entityData);
            }
          }
        } else {
          console.error('Failed to load entity data:', response.statusText);
        }
      } catch (error) {
        console.error('Error loading entity data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEntityData();
  }, [entityId, entityType, mode, isOpen]);

  // Reset form data when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({});
      setError(null);
    }
  }, [isOpen]);

  // Update formData when initialData changes (e.g., when onboarding data loads)
  useEffect(() => {
    // Only update if modal is open and initialData has actual content
    if (!isOpen) return;
    
    if (initialData && Object.keys(initialData).length > 0) {
      // Transform initialData for the form
      const transformedData: FormDataValues = {};
      
      Object.keys(initialData).forEach(key => {
        const value = initialData[key];
        
        // Handle arrays - convert numeric values to strings for multiselect compatibility
        if (Array.isArray(value)) {
          transformedData[key] = value.map((item) => {
            // Convert numbers to strings for multiselect fields
            if (typeof item === 'number') {
              return item.toString();
            }
            return item;
          });
        }
        // Handle nested objects that look like form sections (e.g., {personal_details: {first_name: 'John'}})
        // But preserve objects that are configuration data (e.g., platform_config, geofence_config)
        else if (value && typeof value === 'object' && !Array.isArray(value)) {
          // Check if this looks like a form section (all values are primitives or arrays)
          const isFormSection = Object.values(value).every(v => 
            v === null || 
            v === undefined || 
            typeof v === 'string' || 
            typeof v === 'number' || 
            typeof v === 'boolean' ||
            Array.isArray(v)
          );
          
          if (isFormSection) {
            // Flatten form sections
            Object.keys(value).forEach(fieldKey => {
              transformedData[fieldKey] = value[fieldKey] as FieldValue;
            });
          } else {
            // Preserve complex config objects (like platform_config with nested objects)
            transformedData[key] = value;
          }
        }
        // Handle primitives
        else {
          transformedData[key] = value;
        }
      });
      
      console.log('[DynamicFormModal] Setting form data from initialData:', {
        keys: Object.keys(transformedData),
        sampleData: {
          name: transformedData.name,
          active_days: transformedData.active_days,
          location_ids: transformedData.location_ids,
          is_active: transformedData.is_active
        }
      });
      
      // Fill-only-empty merge: when initialData changes after open (e.g. work-item
      // context resolving late), populate fields that are still empty without
      // discarding values the user already typed.
      setFormData((prev) => {
        const merged: FormDataValues = { ...prev };
        for (const [k, v] of Object.entries(transformedData)) {
          if (merged[k] === undefined || merged[k] === null || merged[k] === '') {
            merged[k] = v;
          }
        }
        return merged;
      });
    }
    // Note: Using JSON.stringify for deep comparison to prevent unnecessary re-renders
    // Only re-run when the actual content of initialData changes, not just the reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialData), isOpen, formKey]);

  const handleDataChange = (data: FormDataValues) => {
    setFormData(data);
  };

  const handleSubmit = async (data: FormDataValues) => {
    console.log('[DynamicFormModal] 🚀 handleSubmit called with data:', data);
    console.log('[DynamicFormModal] Custom onSubmit exists:', !!onSubmit);
    console.log('[DynamicFormModal] entityType:', entityType, 'mode:', mode);
    
    setSaving(true);
    try {
      // If custom onSubmit is provided, use it instead of default behavior
      if (onSubmit) {
        console.log('[DynamicFormModal] 📤 Calling custom onSubmit handler...');
        await onSubmit(data);
        console.log('[DynamicFormModal] ✅ Custom onSubmit completed');
        onSuccess?.(data);
        // NOTE: Do NOT call onClose() here - the parent component with custom onSubmit
        // is responsible for controlling modal close behavior (e.g., showing billing step first)
        setFormData({});
        return;
      }

      console.log('[DynamicFormModal] 📤 Using default submit handler...');

      // Extract geofence assignments and shift_id before sending to main API
      const geofenceAssignments = data.geofence_assignments;
      const shiftId = data.shift_id ? parseInt(String(data.shift_id)) : null;
      const employeeId = data.employee_id || entityId;
      
      // Extract shift_template_id from modules array (total-attendance conditional field)
      let shiftTemplateId: FieldValue = data.shift_template_id || null;
      if (!shiftTemplateId && data.modules && Array.isArray(data.modules)) {
        const attendanceModule = data.modules.find((m) => asObject(m)?.module_key === 'total-attendance');
        const attendanceShiftId = asObject(attendanceModule)?.shift_template_id;
        if (attendanceShiftId) {
          shiftTemplateId = attendanceShiftId as FieldValue;
        }
      }
      
      console.log('Form submission data:', {
        hasGeofenceAssignments: !!geofenceAssignments,
        geofenceCount: Array.isArray(geofenceAssignments) ? geofenceAssignments.length : 0,
        shiftId,
        shiftTemplateId,
        employeeId,
        entityType,
        modules: data.modules
      });
      
      // Remove geofence_assignments and shift_id from main data payload (they're handled separately)
      const { geofence_assignments: _, shift_id: __, shift_name: ___, has_face_template: ____, face_template_count: _____, ...mainData } = data;
      
      // Add shift_template_id to main data if extracted from modules
      if (shiftTemplateId) {
        mainData.shift_template_id = shiftTemplateId;
      }

      // Default CRUD endpoint behavior
      // Determine the API endpoint based on entity type and mode
      let endpoint = '';
      let method = 'POST';

      if (entityType === 'employee') {
        if (mode === 'create') {
          endpoint = '/api/v1/employees';
          method = 'POST';
        } else {
          // Use entityId directly - API endpoint supports employee_id
          endpoint = `/api/v1/employees/${entityId}`;
          method = 'PUT';
        }
      } else if (entityType === 'user') {
        if (mode === 'create') {
          endpoint = '/api/v1/users';
          method = 'POST';
        } else {
          endpoint = `/api/v1/users/${entityId}`;
          method = 'PUT';
        }
      } else if (entityType === 'invitation') {
        if (mode === 'create') {
          endpoint = '/api/v1/user_invitations';
          method = 'POST';
        } else {
          endpoint = `/api/v1/user_invitations/${entityId}`;
          method = 'PUT';
        }
      }

      // Validate endpoint was set
      if (!endpoint) {
        throw new Error(`No API endpoint configured for entity type: ${entityType}`);
      }

      // Get authentication token
      const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
      const token = tokens.access_token;

      if (!token) {
        throw new Error('No authentication token found');
      }

      // Map form field names to API field names
      const apiData: FormDataValues = { ...mainData };
      
      // Map default_role to role (form uses default_role, API expects role)
      if (apiData.default_role !== undefined) {
        apiData.role = apiData.default_role;
        delete apiData.default_role;
      }
      
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...apiData,
          // Only include form_key for employee/user entities, not invitations
          ...(entityType !== 'invitation' && { form_key: formKey })
        }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // For invitations, the API returns the invitation object directly
        // For other entities, check for success field
        if (entityType === 'invitation' || result.success) {
          const responseData = entityType === 'invitation' ? result : result.data;
          
          // If we have employee_id, save related data
          if (employeeId && (entityType === 'employee')) {
            // Save geofence assignments ONLY if the field exists in the form and has data
            if (geofenceAssignments && Array.isArray(geofenceAssignments) && geofenceAssignments.length > 0) {
              try {
                const saveGeofenceAssignments = async (
                  employeeId: number,
                  assignments: unknown[],
                  token: string
                ) => {
                  console.log('Saving geofence assignments:', {
                    employeeId,
                    assignmentCount: assignments.length,
                    assignments
                  });
                  
                  // First, delete existing assignments
                  const deleteResponse = await fetch(`/api/v1/time-attendance/config-assignments?employee_id=${employeeId}`, {
                    method: 'DELETE',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                    }
                  });
                  
                  console.log('Deleted existing assignments:', deleteResponse.ok);

                  // Then create new assignments
                  if (assignments && assignments.length > 0) {
                    for (const assignmentEntry of assignments) {
                      const assignment = asObject(assignmentEntry);
                      if (!assignment) continue;
                      const payload = {
                        employee_id: employeeId,
                        geofence_id: assignment.geofence_id,
                        allowed_platform: assignment.allowed_platform || 'any',
                        allow_offline: assignment.allow_offline ?? true,
                        allow_supervisor_override: assignment.allow_supervisor_override ?? false,
                        allow_enrollment_recapture: assignment.allow_enrollment_recapture ?? true,
                        max_offline: assignment.max_offline || null
                      };
                      
                      console.log('Creating assignment:', payload);
                      
                      const createResponse = await fetch('/api/v1/time-attendance/config-assignments', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify(payload)
                      });
                      
                      const createResult = await createResponse.json();
                      console.log('Assignment creation result:', createResult);
                    }
                  }
                  
                  console.log('Geofence assignments saved successfully');
                };

                await saveGeofenceAssignments(Number(employeeId), geofenceAssignments, token);
              } catch (geoError) {
                console.error('Error saving geofence assignments:', geoError);
                // Continue even if geofence save fails - employee was saved successfully
              }
            }
            
            // NOTE: Shift assignment is now handled directly in the employee PUT endpoint
            // via the shift_template_id field, so we don't need a separate API call
          }
          
          onSuccess?.(responseData);
          onClose();
          
          // Dispatch custom event to trigger employee list refresh
          if (entityType === 'employee') {
            window.dispatchEvent(new CustomEvent('employeeUpdated'));
          }
          
          // Reset form
          setFormData({});
        } else {
          throw new Error(result.error || 'Save failed');
        }
      } else {
        // Try to get error message from response
        try {
          const errorResult = await response.json();
          throw new Error(errorResult.error || `HTTP ${response.status}: ${response.statusText}`);
        } catch (parseError) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save. Please try again.';
      setError(errorMessage);
      // Auto-clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const saveShiftAssignment = async (
    employeeId: number,
    shiftTemplateId: number | null,
    token: string
  ) => {
    console.log('Saving shift assignment:', {
      employeeId,
      shiftTemplateId
    });

    try {
      // Delete all existing shift schedules for this employee_id
      // This ensures we don't create duplicates
      const deleteResponse = await fetch('/api/v1/time-attendance/shifts', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          employee_id: employeeId
        })
      });

      console.log('Deleted existing shift schedules:', deleteResponse.ok);

      // If a shift template is selected, create a new schedule
      if (shiftTemplateId) {
        const payload = {
          employee_id: employeeId,
          template_id: shiftTemplateId,
          shift_date: new Date().toISOString().split('T')[0], // Today's date
          is_active: true, // Set as active
        };

        console.log('Creating shift schedule:', payload);

        const createResponse = await fetch('/api/v1/time-attendance/shifts', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(payload)
        });

        if (!createResponse.ok) {
          throw new Error(`Failed to create shift schedule: ${createResponse.statusText}`);
        }

        const createResult = await createResponse.json();
        console.log('Shift schedule creation result:', createResult);
        
        if (!createResult.success) {
          throw new Error(createResult.error || 'Failed to create shift schedule');
        }
      }

      console.log('Shift assignment saved successfully');
    } catch (error) {
      console.error('Error in saveShiftAssignment:', error);
      throw error;
    }
  };

  const handleClose = () => {
    if (!saving) {
      setFormData({});
      onClose();
    }
  };

  const modalThemeInput = formTheme ?? customSchema?.theme_config ?? null;
  const modalTheme = modalThemeInput ? resolveFormTheme(modalThemeInput) : null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: modalTheme ? modalTheme.bg_color : 'var(--theme-surface)',
          color: modalTheme ? modalTheme.answer_color : 'var(--theme-text-primary)',
          borderColor: modalTheme ? (modalTheme.card_border_color || modalTheme.input_border_color || 'var(--theme-border)') : 'var(--theme-border)'
        }}
        onInteractOutside={(e) => {
          // Prevent closing when clicking on Google Places dropdown
          const target = e.target as HTMLElement;
          if (target.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: modalTheme ? modalTheme.question_color : 'var(--theme-text-primary)' }}>{title}</DialogTitle>
          <DialogDescription style={{ color: modalTheme ? modalTheme.answer_color : 'var(--theme-text-secondary)' }}>
            {subtitle
              ? subtitle
              : customSchema
              ? (customSchema?.description || 'Preview this form template')
              : mode === 'create'
              ? `Fill out the form below to create a new ${entityType}.`
              : mode === 'view'
              ? `View details of this ${entityType}.`
              : `Update the information below to edit this ${entityType}.`
            }
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-primary)' }} />
              <span style={{ color: 'var(--theme-text-secondary)' }}>Loading data...</span>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}
            <DynamicFormBuilder
              formKey={formKey}
              formData={formData}
              onDataChange={handleDataChange}
              onSubmit={handleSubmit}
              onCancel={handleClose}
              mode={mode}
              entityType={entityType}
              entityId={entityId}
              initialData={initialData}
              showActions={true}
              showHeader={false}
              className="space-y-4"
              customSchema={customSchema}
              formTheme={formTheme}
              enableImageUpload
            />
          </div>
        )}

        {saving && (
          <div 
            className="absolute inset-0 backdrop-blur-sm flex items-center justify-center"
            style={{ 
              backgroundColor: 'rgba(var(--theme-surface), 0.8)',
              color: 'var(--theme-text-primary)'
            }}
          >
            <div className="flex items-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Saving...</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}